import { QUESTION_TYPES } from '@api/instant_survey';
import type { Question } from '@api/instant_survey/types';

import {
  SCREENER_HIERARCHY_OPTIONS,
  SCREENER_PROMPT_TOKEN_REGEX,
  SCREENER_URL_VARIABLE_PROMPT_KEY,
} from './constants';

type PromptValueMap = Record<string, string>;

const addPromptKeysFromText = (text: string | undefined, keys: Set<string>) => {
  if (!text) return;

  SCREENER_PROMPT_TOKEN_REGEX.lastIndex = 0;
  let match;

  while ((match = SCREENER_PROMPT_TOKEN_REGEX.exec(text)) !== null) {
    keys.add(match[1]);
  }
};

export const getQuestionTypeLabel = (type: string) => {
  if (type === QUESTION_TYPES.RADIO.value) return QUESTION_TYPES.RADIO.name;
  return type;
};

/**
 * Collects editable screener prompt keys from the question, answers, and subquestions.
 * Only plain mustache tokens like {{place}} are treated as editable prompt keys.
 */
export const getScreenerPromptKeys = (question?: null | Question): string[] => {
  const keys = new Set<string>();

  const collectQuestionKeys = (nextQuestion?: null | Question) => {
    if (!nextQuestion) return;

    addPromptKeysFromText(nextQuestion.text, keys);

    nextQuestion.answers?.forEach((answer) => {
      addPromptKeysFromText(answer.text, keys);
    });

    nextQuestion.subQuestions?.forEach(collectQuestionKeys);
  };

  collectQuestionKeys(question);

  return Array.from(keys);
};

/**
 * Builds the editable prompt state for the preview modal from any saved backend values.
 * The url_variable prompt starts with the first hierarchy option when the question includes it.
 */
export const getInitialScreenerPromptValues = (question: null | Question): PromptValueMap => {
  const promptKeys = getScreenerPromptKeys(question);
  const promptValues: PromptValueMap = { ...(question?.screenerValueMap || {}) };
  const firstHierarchyOption = SCREENER_HIERARCHY_OPTIONS[0];

  if (
    promptKeys.includes(SCREENER_URL_VARIABLE_PROMPT_KEY) &&
    firstHierarchyOption?.value &&
    !promptValues[SCREENER_URL_VARIABLE_PROMPT_KEY]?.trim()
  ) {
    promptValues[SCREENER_URL_VARIABLE_PROMPT_KEY] = `[${firstHierarchyOption.value}]`;
  }

  return promptValues;
};

/**
 * Creates the screener_value_map payload by keeping only filled prompt values.
 * Question and option text stay tokenized; this map carries the user's replacements.
 */
export const buildScreenerValueMap = (
  promptKeys: string[],
  promptValues: PromptValueMap
): PromptValueMap =>
  promptKeys.reduce<PromptValueMap>((valueMap, promptKey) => {
    const value = promptValues[promptKey]?.trim();

    if (value) {
      valueMap[promptKey] = value;
    }

    return valueMap;
  }, {});

/**
 * Hydrates tokenized screener text for display from a saved value map.
 * Missing or blank map entries leave their original {{token}} text untouched.
 */
export const replaceScreenerPromptTokensWithValueMap = (
  text: string | undefined,
  screenerValueMap: PromptValueMap = {}
) => {
  if (!text) return '';

  SCREENER_PROMPT_TOKEN_REGEX.lastIndex = 0;

  return text.replace(SCREENER_PROMPT_TOKEN_REGEX, (token, promptKey) => {
    const value = screenerValueMap[promptKey]?.trim();

    return value || token;
  });
};

/**
 * Returns the text shown inside an editable prompt before the final value map is built.
 * Untouched empty prompts show their token key as an affordance; once touched, an empty value stays blank.
 */
export const getScreenerPromptDisplayValue = ({
  promptKey,
  promptValues,
  touchedPromptKeys,
}: {
  promptKey: string;
  promptValues: PromptValueMap;
  touchedPromptKeys: Set<string>;
}) => {
  if (Object.prototype.hasOwnProperty.call(promptValues, promptKey)) {
    return promptValues[promptKey];
  }

  return touchedPromptKeys.has(promptKey) ? '' : promptKey;
};

/**
 * Converts prompt keys into readable labels for validation messages.
 * For example, "purchase_window" becomes "Purchase window".
 */
export const humanizeScreenerPromptKey = (promptKey: string) =>
  promptKey
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (character) => character.toUpperCase());

export const toScreenerPromptTestId = (promptKey: string) =>
  `screener-prompt-${promptKey.replace(/_/g, '-')}`;
